import React, { useState } from 'react';
import { X, Save, Lightbulb } from 'lucide-react';
import { createSuggestedPrompt } from '../services/database';
import { useAuth } from '../contexts/AuthContext';

interface SuggestPromptModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const SuggestPromptModal: React.FC<SuggestPromptModalProps> = ({ isVisible, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    prompt: '',
    category: '',
    tags: [] as string[]
  });
  const [tagsString, setTagsString] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.title.trim() || !formData.prompt.trim()) {
      setError('Title and prompt text are required');
      return;
    }

    if (!user) {
      setError('You must be logged in to suggest a prompt');
      return;
    }

    setIsLoading(true);
    try {
      const result = await createSuggestedPrompt({
        ...formData,
        tags: tagsString.split(',').map(s => s.trim()).filter(s => s),
        submittedBy: user.name
      });

      if (result) {
        // Reset form
        setFormData({
          title: '',
          description: '',
          prompt: '',
          category: '',
          tags: []
        });
        setTagsString('');
        onSuccess?.();
        onClose();
      } else {
        setError('Failed to submit suggestion. Please try again.');
      }
    } catch (err) {
      setError('Failed to submit suggestion. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[#21282D] rounded-lg p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-yellow-900/20 rounded-full flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-yellow-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-100">Suggest a Prompt</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-300 transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-100 mb-2">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
                placeholder="Enter prompt title"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-100 mb-2">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
              >
                <option value="">Select category</option>
                <option value="strategy">Strategy & Planning</option>
                <option value="creative">Creative & Messaging</option>
                <option value="data">Data & Performance</option>
                <option value="client">Client Communication</option>
                <option value="operations">Operations & Productivity</option>
                <option value="general">General</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-100 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
              placeholder="Describe what this prompt does..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-100 mb-2">Prompt Text *</label>
            <textarea
              value={formData.prompt}
              onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
              rows={6}
              className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100 font-mono"
              placeholder="Enter your prompt text here..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-100 mb-2">Tags (comma-separated)</label>
            <input
              type="text"
              value={tagsString}
              onChange={(e) => setTagsString(e.target.value)}
              className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100"
              placeholder="e.g., strategy, planning, creative"
            />
          </div>

          {error && (
            <div className="bg-red-900/20 border border-red-600 rounded-lg p-3">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}

          <div className="bg-blue-900/20 border border-blue-600 rounded-lg p-4">
            <p className="text-blue-300 text-sm">
              <strong>How it works:</strong>
              <br />
              • Your suggestion will be reviewed by an administrator
              <br />
              • If approved, it will be added to the prompt library
              <br />
              • You'll be notified when your suggestion is reviewed
            </p>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-300 hover:text-gray-100 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !formData.title.trim() || !formData.prompt.trim()}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center space-x-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Submit Suggestion</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SuggestPromptModal;






